import React, { useEffect, useState } from 'react';
import { Icon } from './base/Icon';
import { useKeyboardShortcuts, type ShortcutSpec } from './useKeyboardShortcuts';

/**
 * KeyboardShortcutHelp — กด ? เพื่อเปิด
 *
 * เหตุผลที่ต้องมี: ถ้าคีย์ลัดไม่มีที่ให้ค้นหา เจ้าหน้าที่จะไม่ใช้
 * และงานตรวจคิวจะกลับไปช้าเท่าเดิม
 *
 * รับรายการคีย์ลัดของหน้าปัจจุบันเข้ามา (จาก QUEUE_SHORTCUTS + ที่หน้านั้นเพิ่มเอง)
 * เพื่อไม่แสดงคีย์ลัดที่หน้านั้นไม่มี
 */
export interface KeyboardShortcutHelpProps {
  /** คีย์ลัดที่ใช้ได้ในหน้านี้ */
  shortcuts: Array<Pick<ShortcutSpec, 'key' | 'description' | 'group'>>;
  /** เปิดจากภายนอกได้ด้วย (เช่น เมนูช่วยเหลือ) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const KEY_LABEL: Record<string, string> = {
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  Enter: 'Enter', Escape: 'Esc', Home: 'Home', End: 'End',
};

export const KeyboardShortcutHelp: React.FC<KeyboardShortcutHelpProps> = ({ shortcuts, open, onOpenChange }) => {
  const [inner, setInner] = useState(false);
  const isOpen = open ?? inner;
  const setOpen = (v: boolean) => { onOpenChange?.(v); if (open === undefined) setInner(v); };

  useKeyboardShortcuts([
    { key: '?', description: 'เปิดรายการคีย์ลัดนี้', group: 'ทั่วไป', handler: () => setOpen(!isOpen) },
    { key: 'Escape', description: 'ปิดรายการคีย์ลัด', group: 'ทั่วไป', handler: () => setOpen(false), disabled: !isOpen },
  ]);

  useEffect(() => { if (!isOpen) return; }, [isOpen]);

  if (!isOpen) return null;

  /** จัดกลุ่มและรวมคีย์ที่ทำงานเดียวกัน (J และ ↓) ให้อยู่บรรทัดเดียว */
  const groups = new Map<string, Map<string, string[]>>();
  for (const s of shortcuts) {
    const g = s.group ?? 'ทั่วไป';
    if (!groups.has(g)) groups.set(g, new Map());
    const byDesc = groups.get(g)!;
    if (!byDesc.has(s.description)) byDesc.set(s.description, []);
    byDesc.get(s.description)!.push(KEY_LABEL[s.key] ?? s.key.toUpperCase());
  }

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[var(--scrim)] p-[var(--space-4)]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcut-help-title"
        className="flex max-h-[80vh] w-full max-w-[640px] flex-col rounded-[var(--radius-lg)] bg-[var(--surface-0)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-modal)]"
      >
        <header className="flex items-center gap-[var(--space-2)] border-b border-[var(--line-200)] px-[var(--space-5)] py-[var(--space-3)]">
          <Icon name="keyboard" size={22} className="text-[var(--blue-700)]" />
          <h2 id="shortcut-help-title" className="flex-1 text-[length:var(--text-page-title)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
            คีย์ลัด
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="ปิด (Esc)"
            data-autofocus
            className="inline-flex h-[var(--hit-control)] w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-600)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto px-[var(--space-5)] py-[var(--space-3)]">
          {[...groups.entries()].map(([group, rows]) => (
            <section key={group} className="mb-[var(--space-4)]">
              <h3 className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] uppercase tracking-wide text-[var(--ink-600)]">
                {group}
              </h3>
              <dl className="flex flex-col gap-[var(--space-1)]">
                {[...rows.entries()].map(([description, keys]) => (
                  <div key={description} className="flex items-baseline justify-between gap-[var(--space-3)] border-b border-[var(--line-200)] py-[var(--space-15)]">
                    <dt className="text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-900)]">{description}</dt>
                    <dd className="flex shrink-0 items-center gap-[var(--space-1)]">
                      {keys.map((k, i) => (
                        <React.Fragment key={k}>
                          {i > 0 && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">หรือ</span>}
                          <kbd className="rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-100)] px-[var(--space-15)] py-[var(--space-05)] font-[family-name:var(--font-num)] text-[length:var(--text-table-sm)] text-[var(--ink-900)]">
                            {k}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <footer className="border-t border-[var(--line-200)] px-[var(--space-5)] py-[var(--space-25)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">
          กด <kbd className="rounded-[var(--radius-xs)] border border-[var(--line-300)] bg-[var(--surface-100)] px-1 font-[family-name:var(--font-num)]">?</kbd> ที่หน้าไหนก็ได้เพื่อเปิดรายการนี้ · คีย์ลัดไม่ทำงานขณะพิมพ์ในช่องกรอก
        </footer>
      </div>
    </div>
  );
};
